import { Card, Space } from '@procurify/ui'

import { Tabs } from '@webapp/core/components/Tabs'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { TermsMethodsKinds } from '@webapp/modules/purchasing/entities/enums'
import { PODisclaimerLabelsView } from '@webapp/modules/purchasing/feature-sets/PODisclaimerLabelsView'
import { POPDFCustomFieldDisplayView } from '@webapp/modules/purchasing/feature-sets/POPDFCustomFieldDisplayView'
import { POPDFLabelsView } from '@webapp/modules/purchasing/feature-sets/POPDFLabelsView'
import { POTermsMethodsView } from '@webapp/modules/purchasing/feature-sets/POTermsMethodsView'
import { useExternalPONumberingSettingPresenter } from '@webapp/modules/purchasing/features/get-external-po-numbering-setting/presenters/UseExternalPONumberingSettingPresenter'
import { ExternalPONumberingTogglePresenter } from '@webapp/modules/purchasing/features/set-external-po-numbering/presenters/ExternalPONumberingTogglePresenter'
import messages from './messages'
import {
  type CustomizePurchaseOrderFormTab,
  useCustomizePurchaseOrderFormView,
} from './useCustomizePurchaseOrderFormView'

/**
 * External PO Numbering card, mounted ONLY when the `enable_auto_po` feature
 * switch is on (BHV-003) so the preference query hook is never called while
 * the switch is off. Bridges the query feature's value to the command
 * presenter (feature-level CQS).
 */
const ExternalPONumberingSection = () => {
  const { isEnabled, isLoading } = useExternalPONumberingSettingPresenter()

  if (isLoading) return null

  return (
    <Card>
      <Card.Content>
        <ExternalPONumberingTogglePresenter isEnabled={isEnabled} />
      </Card.Content>
    </Card>
  )
}

/**
 * Feature Set: Customize Purchase Order Form View (Page)
 *
 * Orchestrates, top to bottom:
 * - External PO Numbering card (`enable_auto_po` gate — BHV-003)
 * - Six-tab bar driven by the `:tab` route param (BHV-002); custom field
 *   management moved to the Main Custom Field Setup page (PLAT-4936)
 * - Card → Card.Content containing the active section Feature Set
 */
export const CustomizePurchaseOrderFormView = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const {
    states: { activeTab, showExternalPONumbering },
  } = useCustomizePurchaseOrderFormView()

  const renderActiveSection = (tab: CustomizePurchaseOrderFormTab) => {
    switch (tab) {
      case TermsMethodsKinds.SHIPPING_TERMS:
      case TermsMethodsKinds.PAYMENT_TERMS:
      case TermsMethodsKinds.SHIPPING_METHODS:
        return <POTermsMethodsView kind={tab} />
      case 'po-pdf-labels':
        return <POPDFLabelsView />
      case 'po-pdf-custom-fields':
        return <POPDFCustomFieldDisplayView />
      case 'disclaimer':
      default:
        return <PODisclaimerLabelsView />
    }
  }

  return (
    <Space>
      {showExternalPONumbering && <ExternalPONumberingSection />}

      <div>
        <Tabs>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', { tab: 'disclaimer' })}
          >
            {intl.formatMessage(messages.disclaimerTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', {
              tab: TermsMethodsKinds.SHIPPING_TERMS,
            })}
          >
            {intl.formatMessage(messages.shippingTermsTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', {
              tab: TermsMethodsKinds.PAYMENT_TERMS,
            })}
          >
            {intl.formatMessage(messages.paymentTermsTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', {
              tab: TermsMethodsKinds.SHIPPING_METHODS,
            })}
          >
            {intl.formatMessage(messages.shippingMethodsTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', { tab: 'po-pdf-labels' })}
          >
            {intl.formatMessage(messages.poPdfLabelsTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CustomizePurchaseOrderFormPage', {
              tab: 'po-pdf-custom-fields',
            })}
          >
            {intl.formatMessage(messages.poPdfCustomFieldsTabLabel)}
          </Tabs.TabLink>
        </Tabs>
        <Card>
          <Card.Content>{renderActiveSection(activeTab)}</Card.Content>
        </Card>
      </div>
    </Space>
  )
}

CustomizePurchaseOrderFormView.displayName = 'CustomizePurchaseOrderFormView'
